import { useMemo, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { EmptyState } from '@/components/UI';
import type { AeroCandidateReviewRow } from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import { type AeroObsPalette, buildAeroObsKey, getAeroObsTone } from '../aeroObsPalette';

interface AeroResultsChartsProps {
    palette: AeroObsPalette;
    results: AeroCandidateReviewRow[];
}

const TOOLTIP_STYLE = { background: '#18181b', border: '1px solid #3f3f46', color: '#f4f4f5' } as const;

const buildWinRateBuckets = (results: AeroCandidateReviewRow[]) => {
    const isRatio = results.length > 0 && results.every((result) => result.winRate <= 1.5);
    const buckets = Array.from({ length: 10 }, (_, index) => ({ name: `${index * 10}-${index * 10 + 10}`, count: 0 }));

    for (const result of results) {
        const winRate = isRatio ? result.winRate * 100 : result.winRate;
        const bucketIndex = Math.min(9, Math.max(0, Math.floor(winRate / 10)));

        buckets[bucketIndex].count += 1;
    }

    return buckets;
};

const PROFIT_FACTOR_BUCKETS: { name: string; min: number; max: number }[] = [
    { name: '<0.5', min: -Infinity, max: 0.5 },
    { name: '0.5-1', min: 0.5, max: 1 },
    { name: '1-1.5', min: 1, max: 1.5 },
    { name: '1.5-2', min: 1.5, max: 2 },
    { name: '2-3', min: 2, max: 3 },
    { name: '3+', min: 3, max: Infinity }
];

const buildProfitFactorBuckets = (results: AeroCandidateReviewRow[]) =>
    PROFIT_FACTOR_BUCKETS.map((bucket) => ({
        name: bucket.name,
        min: bucket.min,
        count: results.filter((result) => {
            const profitFactor =
                result.optionReturnSelectionPolicy === 'upper_winsorized_v1'
                    ? (result.winsorizedProfitFactor ?? result.profitFactor)
                    : result.profitFactor;

            return profitFactor >= bucket.min && profitFactor < bucket.max;
        }).length
    }));

interface TopByObsRow {
    obsKey: string;
    label: string;
    fill: string;
    score: number;
}

const buildTopScoreByObs = (palette: AeroObsPalette, results: AeroCandidateReviewRow[]): TopByObsRow[] => {
    const bestByKey = new Map<string, number>();

    for (const candidate of results) {
        const key = buildAeroObsKey(candidate.optionBuyingStrategyId);
        const current = bestByKey.get(key);
        if (current === undefined || candidate.selectedLiveScore > current) bestByKey.set(key, candidate.selectedLiveScore);
    }

    return palette.tones
        .map((tone) => {
            const score = bestByKey.get(tone.key);
            if (score === undefined) return null;

            return {
                obsKey: tone.key,
                label: tone.shortLabel,
                fill: tone.barFill,
                score: Math.round(score * 100) / 100
            };
        })
        .filter((row): row is TopByObsRow => row !== null);
};

interface ScatterSeries {
    obsKey: string;
    name: string;
    fill: string;
    data: { drawdown: number; avgProfit: number; liveScore: number }[];
}

const buildScatterSeries = (palette: AeroObsPalette, results: AeroCandidateReviewRow[]): ScatterSeries[] => {
    const grouped = new Map<string, ScatterSeries>();

    for (const tone of palette.tones) {
        grouped.set(tone.key, { obsKey: tone.key, name: tone.shortLabel, fill: tone.scatterFill, data: [] });
    }

    for (const candidate of results.slice(0, 200)) {
        const selectedDrawdown =
            candidate.optionReturnSelectionPolicy === 'upper_winsorized_v1'
                ? (candidate.winsorizedDrawdown ?? candidate.drawdown)
                : candidate.drawdown;
        // Aggregate-only variants have no equity-curve drawdown; skip them from the drawdown
        // scatter axis rather than plotting a fabricated value.
        if (selectedDrawdown == null) continue;

        const key = buildAeroObsKey(candidate.optionBuyingStrategyId);
        const tone = getAeroObsTone(palette, candidate.optionBuyingStrategyId);
        const series = grouped.get(key) ?? { obsKey: key, name: tone.shortLabel, fill: tone.scatterFill, data: [] };

        series.data.push({
            drawdown: selectedDrawdown,
            avgProfit: candidate.selectedAvgProfit,
            liveScore: Math.round(candidate.selectedLiveScore * 100) / 100
        });

        if (!grouped.has(key)) grouped.set(key, series);
    }

    return Array.from(grouped.values()).filter((series) => series.data.length > 0);
};

const ChartPanel = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
        <div className="mb-3 text-sm font-medium text-zinc-100">{title}</div>
        {children}
    </div>
);

const ChartLegend = ({ series }: { series: { name: string; fill: string }[] }) => {
    if (series.length <= 1) return null;

    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-400">
            {series.map((entry) => (
                <span className="inline-flex items-center gap-1" key={entry.name}>
                    <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: entry.fill }} />
                    {entry.name}
                </span>
            ))}
        </div>
    );
};

const AeroResultsCharts = ({ palette, results }: AeroResultsChartsProps) => {
    const rankedData = useMemo(
        () =>
            results.slice(0, 25).map((candidate, index) => ({
                name: String(index + 1),
                score: Math.round(candidate.selectedLiveScore * 100) / 100,
                avgProfit: candidate.selectedAvgProfit,
                drawdown:
                    candidate.optionReturnSelectionPolicy === 'upper_winsorized_v1'
                        ? (candidate.winsorizedDrawdown ?? candidate.drawdown)
                        : candidate.drawdown,
                fill: getAeroObsTone(palette, candidate.optionBuyingStrategyId).barFill
            })),
        [palette, results]
    );

    const scatterSeries = useMemo(() => buildScatterSeries(palette, results), [palette, results]);
    const winRateBuckets = useMemo(() => buildWinRateBuckets(results), [results]);
    const profitFactorBuckets = useMemo(() => buildProfitFactorBuckets(results), [results]);
    const topByObs = useMemo(() => buildTopScoreByObs(palette, results), [palette, results]);

    if (results.length === 0) {
        return <EmptyState size="sm" title="No chart data is available." />;
    }

    return (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {palette.hasMultipleAxes && topByObs.length > 1 && (
                <ChartPanel title="Top selected score by axis">
                    <ResponsiveContainer height={Math.max(180, topByObs.length * 36 + 60)} width="100%">
                        <BarChart data={topByObs} layout="vertical">
                            <CartesianGrid stroke="#3f3f46" strokeDasharray="3 3" />
                            <XAxis stroke="#a1a1aa" type="number" />
                            <YAxis dataKey="label" stroke="#a1a1aa" type="category" width={120} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} />
                            <Bar dataKey="score" radius={[0, 3, 3, 0]}>
                                {topByObs.map((row) => (
                                    <Cell fill={row.fill} key={row.obsKey} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartPanel>
            )}

            <ChartPanel title="Selected score in current order (top 25)">
                <ResponsiveContainer height={260} width="100%">
                    <BarChart data={rankedData}>
                        <CartesianGrid stroke="#3f3f46" strokeDasharray="3 3" />
                        <XAxis dataKey="name" stroke="#a1a1aa" />
                        <YAxis stroke="#a1a1aa" />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="score" radius={[3, 3, 0, 0]}>
                            {rankedData.map((row, index) => (
                                <Cell fill={row.fill} key={`${row.name}-${index}`} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <ChartLegend series={palette.tones.map((tone) => ({ name: tone.shortLabel, fill: tone.barFill }))} />
            </ChartPanel>

            <ChartPanel title="Selected avg profit vs selected drawdown">
                <ResponsiveContainer height={260} width="100%">
                    <ScatterChart>
                        <CartesianGrid stroke="#3f3f46" strokeDasharray="3 3" />
                        <XAxis dataKey="drawdown" name="Drawdown %" stroke="#a1a1aa" type="number" unit="%" />
                        <YAxis dataKey="avgProfit" name="Selected avg profit %" stroke="#a1a1aa" type="number" unit="%" />
                        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: '#71717a' }} />
                        {scatterSeries.map((series) => (
                            <Scatter data={series.data} fill={series.fill} key={series.obsKey} name={series.name} />
                        ))}
                    </ScatterChart>
                </ResponsiveContainer>
                <ChartLegend series={scatterSeries.map((series) => ({ name: series.name, fill: series.fill }))} />
            </ChartPanel>

            <ChartPanel title="Win rate distribution (%)">
                <ResponsiveContainer height={240} width="100%">
                    <BarChart data={winRateBuckets}>
                        <CartesianGrid stroke="#3f3f46" strokeDasharray="3 3" />
                        <XAxis dataKey="name" stroke="#a1a1aa" />
                        <YAxis allowDecimals={false} stroke="#a1a1aa" />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" fill="#0891b2" radius={[3, 3, 0, 0]}>
                            {winRateBuckets.map((bucket) => (
                                <Cell fill={bucket.count > 0 ? '#0891b2' : '#27272a'} key={bucket.name} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartPanel>

            <ChartPanel title="Selected profit factor distribution">
                <ResponsiveContainer height={240} width="100%">
                    <BarChart data={profitFactorBuckets}>
                        <CartesianGrid stroke="#3f3f46" strokeDasharray="3 3" />
                        <XAxis dataKey="name" stroke="#a1a1aa" />
                        <YAxis allowDecimals={false} stroke="#a1a1aa" />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                            {profitFactorBuckets.map((bucket) => (
                                <Cell fill={bucket.min >= 1 ? '#16a34a' : '#b45309'} key={bucket.name} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartPanel>
        </div>
    );
};

export default AeroResultsCharts;
